import { BULK_ACTIONS } from '@beta-assur/shared';
import { useState } from 'react';
import { ActionError } from '../components/states';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Select } from '../components/ui/input';
import { useApi } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { useApiQuery } from '../lib/query';
import type { BulkResult, ScenarioList } from '../lib/types';

// Actions de masse (BA-040, spec 08) : toujours précédées d'un récapitulatif (simulation).

type Action = (typeof BULK_ACTIONS)[number];

export function BulkBar({ ids, onDone }: { ids: string[]; onDone: () => void }) {
  const { t, count } = useI18n();
  const api = useApi();
  const [action, setAction] = useState<Action>('pause');
  const [scenarioId, setScenarioId] = useState('');
  const [method, setMethod] = useState('agency');
  const [recap, setRecap] = useState<BulkResult | null>(null);
  const [done, setDone] = useState<BulkResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const scenarios = useApiQuery<ScenarioList>(
    action === 'change_scenario' ? '/v1/scenarios' : null,
  );
  const body = (dryRun: boolean) => ({
    ids,
    action,
    dryRun,
    ...(action === 'change_scenario' ? { scenarioId } : {}),
    ...(action === 'mark_paid' ? { method } : {}),
  });

  async function run(dryRun: boolean) {
    setError(null);
    try {
      const result = await api.request<BulkResult>('POST', '/v1/installments/bulk', {
        body: body(dryRun),
      });
      if (dryRun) setRecap(result);
      else {
        setRecap(null);
        setDone(result);
        onDone();
      }
    } catch (err) {
      setError(err as Error);
    }
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-border bg-accent/40 p-3"
      role="region"
      aria-label={count(ids.length, t.bulk.selected)}
    >
      <span className="text-sm font-medium">{count(ids.length, t.bulk.selected)}</span>
      <Select
        aria-label={t.bulk.action}
        className="w-auto"
        value={action}
        onChange={(e) => setAction(e.target.value as Action)}
      >
        {BULK_ACTIONS.map((a) => (
          <option key={a} value={a}>
            {t.bulk.actions[a]}
          </option>
        ))}
      </Select>
      {action === 'change_scenario' ? (
        <Select
          aria-label={t.bulk.scenario}
          className="w-auto"
          value={scenarioId}
          onChange={(e) => setScenarioId(e.target.value)}
        >
          <option value="">-</option>
          {(scenarios.data?.items ?? [])
            .filter((s) => s.status === 'published')
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </Select>
      ) : null}
      {action === 'mark_paid' ? (
        <Select
          aria-label={t.payments.method}
          className="w-auto"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
        >
          {(['agency', 'bank', 'check', 'other'] as const).map((m) => (
            <option key={m} value={m}>
              {t.paymentMethods[m]}
            </option>
          ))}
        </Select>
      ) : null}
      <Button
        size="sm"
        disabled={action === 'change_scenario' && !scenarioId}
        onClick={() => void run(true)}
      >
        {t.bulk.preview}
      </Button>
      {done ? (
        <span role="status" className="text-sm text-success">
          {count(done.applied, t.bulk.applied)}
        </span>
      ) : null}
      <ActionError error={error} />
      <Dialog
        open={recap !== null}
        onOpenChange={(open) => !open && setRecap(null)}
        title={`${t.bulk.recap} : ${t.bulk.actions[action]}`}
      >
        {recap ? (
          <div className="flex flex-col gap-3 text-sm">
            <p>
              <strong>{count(recap.applicable, t.bulk.applicable)}</strong>
            </p>
            {Object.entries(recap.skipped).length ? (
              <ul className="list-disc ps-5 text-muted-foreground">
                {Object.entries(recap.skipped).map(([reason, n]) => (
                  <li key={reason}>
                    {count(n, t.bulk.skipped)} : {reason}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRecap(null)}>
                {t.common.cancel}
              </Button>
              <Button disabled={recap.applicable === 0} onClick={() => void run(false)}>
                {t.bulk.apply}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
